<template lang="pug">
  .rogue-a11y-container
    #skiplink(tabindex="0")
    .focus-test(tabindex="0" @focus="onFocus()")
    .main-container-wide(
      v-if="focus && !$route.meta.isFunneled"
      @keydown.esc="focus=false"
    )
      .rogue-a11y
        p {{__('Quick Links')}}
        template(v-if="routeType === 'category' || routeType === 'Search'")
          a(href="#category-results") {{__('Skip to Results')}}
          a(href="#category-filters") {{__('Skip to Filters')}}
        template(v-else-if="routeType === 'product'")
          a(href="#pdp-rhpa") {{__('Skip to Purchase Options')}}
          a(href="#pdp-tabs") {{__('Skip to Product Description')}}
          a(href="#customer-reviews") {{__('Skip to Product Reviews')}}
        template(v-else)
          a(href="#main-content") {{__('Skip to Content')}}
        template(v-if="customerId")
          rogue-link(to="/account/dashboard" v-if="$route.path !== '/account/dashboard'") {{__('My Account') }}
          button.link(@click="logout()") {{__('Log Out') }}
        template(v-else)
          rogue-link(to="/login") {{ __('Log In') }}
        rogue-link(to="/cart" v-if="cart && cart.items.length && $route.path !== '/cart'")
          | {{__('Open Cart (Items: {itemCount})', {itemCount: cart.items.length})}}
        rogue-link(to="/checkout" v-if="cart && cart.items.length && $route.path.indexOf('/checkout') !== 0")
          | {{__('Start Checkout')}}
        form(role="search" action="/search" method="get")
          RogueInput.search-bar(type="text" label="Site Search" keydown.enter="goSearch()" name="q" id="a11y-search" v-model="search" )
          RogueButton.search(aria-label="Search" type="submit" color="red" size="xs" @click="goSearch()") {{__('Search')}}
        Checkbox#contrast-checkbox(label="High Contrast Mode" :modelValue="highContrast" @update:modelValue="setHighContrast($event)" )
</template>
<script lang="ts" setup>
import { nextTick, onMounted, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useListStore } from '@/js/stores/listStore'
import { getCookie, setCookie } from '@/js/services/Utils'
import { useRogue } from '@/js/services/Rogue'

defineProps<{highContrast: boolean}>()

const route = useRoute()
const router = useRouter()

const search = ref('')
const focus = ref(false)

const emit = defineEmits<{highContrast: [val: boolean]}>()

const rogue = useRogue()
const customerStore = useCustomerStore()
const listStore = useListStore()

const cart = computed(() => listStore.cart)
const customerId = computed(() => customerStore._id)

const routeType = ref('CMS')

onMounted(() => {
  const highContrast = getCookie('highcontrast') === '1'

  if (highContrast) {
    emit('highContrast', true)
  }
})

function goSearch() {
  router.push({ path: '/search', query: { q: search.value } })
}

function logout() {
  customerStore.logout(rogue)
  router.push('/')
}

function setHighContrast(val:boolean) {
  emit('highContrast', val)
  setCookie('highcontrast', val ? '1' : '0', 365)
}

function onFocus() {
  focus.value = true
  routeType.value = (route.meta?.type as string) || 'CMS'

  document.querySelector('body')?.classList.add('kbd-a11y')
  // Focus on the first link
  nextTick(() => document.querySelector('.rogue-a11y-container a')?.focus())
}

</script>
<style lang="scss" scoped>
.rogue-a11y-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 9999;
}

.main-container-wide {
  position: relative;
}

.rogue-a11y {
  top: 0;
  height: 0;
  overflow: hidden;
  position: absolute;
  background: $color-white;
  pointer-events: none;
  border: 1px solid $color-gray-dark;
  opacity: 0;
  border-top: none;
  right: 0;
  padding: $space-s;
  transition: height 0.2s linear;

  &:focus-within, &:focus, &:active, &:hover {
    opacity: 1;
    height: auto;
    pointer-events: all;
  }

  a, button {
    display: block;
    margin-bottom: $space-xs;
  }

  &:focus-within, &:focus, &:active, &:hover {
    opacity: 1;
    pointer-events: all;
    transition: height 0.2s linear;
    height: auto;
  }

  form {
    display: flex;
    align-items: flex-end;
    padding: $space-xs 0;
    width: 100%;
  }

  .search {
    margin-bottom: 0;
  }

  .search-bar {
    margin-right: $space-s;
    flex-grow: 1;
    margin-bottom: 0;
  }
}

.notices {
  pointer-events: none;
  opacity: 0;
  position: absolute;
  top: -400px;
}

</style>
